import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Rss,
  Plus,
  Trash2,
  RefreshCw,
  X,
  Loader2,
  Ban,
  Link,
  Pencil,
  AlertCircle,
} from 'lucide-react';
import { TwitterIcon, InstagramIcon } from './PlatformIcons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { socialMediaApi } from '../api';
import { FEATURE_FLAGS } from '../feature-flags';
import type {
  InspirationPlatform,
  SocialMediaCompetitorItem,
  SocialMediaRssFeedItem,
} from '@/lib/api';

// ── Platform icon map ──────────────────────────────────────────────────────

const PLATFORM_ICONS: Record<string, React.ReactNode> = {
  twitter: <TwitterIcon className="h-3.5 w-3.5" />,
  instagram: <InstagramIcon className="h-3.5 w-3.5" />,
  tiktok: (
    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.79 1.54V6.79a4.85 4.85 0 01-1.02-.1z" />
    </svg>
  ),
};

const PLATFORM_LABELS: Record<string, string> = {
  twitter: 'Twitter / X',
  instagram: 'Instagram',
  tiktok: 'TikTok',
};

// ── ContentSourcesModal ────────────────────────────────────────────────────

interface ContentSourcesModalProps {
  competitors: SocialMediaCompetitorItem[];
  rssFeeds: SocialMediaRssFeedItem[];
  onClose: () => void;
  onRefresh: () => void;
}

type Tab = 'competitors' | 'rss' | 'manual';
type CompetitorPlatform = Extract<InspirationPlatform, 'twitter' | 'instagram' | 'tiktok'>;

export function ContentSourcesModal({
  competitors,
  rssFeeds,
  onClose,
  onRefresh,
}: ContentSourcesModalProps) {
  const [tab, setTab] = useState<Tab>('competitors');

  // Competitor form
  const [compPlatform, setCompPlatform] = useState<CompetitorPlatform>('twitter');
  const [compUsername, setCompUsername] = useState('');
  const [compLoading, setCompLoading] = useState(false);
  const [compError, setCompError] = useState<string | null>(null);

  // RSS form
  const [rssTitle, setRssTitle] = useState('');
  const [rssUrl, setRssUrl] = useState('');
  const [rssLoading, setRssLoading] = useState(false);
  const [rssError, setRssError] = useState<string | null>(null);

  // Manual source form
  const [manualUrl, setManualUrl] = useState('');
  const [manualText, setManualText] = useState('');
  const [manualTitle, setManualTitle] = useState('');
  const [manualFetchLoading, setManualFetchLoading] = useState(false);
  const [manualFetchError, setManualFetchError] = useState<string | null>(null);
  const [manualShowTextarea, setManualShowTextarea] = useState(false);
  const [manualLoading, setManualLoading] = useState(false);
  const [manualError, setManualError] = useState<string | null>(null);
  const [manualSuccess, setManualSuccess] = useState(false);

  // Deletion / fetch loading states
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [fetchingId, setFetchingId] = useState<string | null>(null);
  const [abortingId, setAbortingId] = useState<string | null>(null);

  // Poll every 5s while any competitor is in the 'fetching' state
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;
  useEffect(() => {
    const hasFetching = competitors.some((c) => c.fetchStatus === 'fetching');
    if (!hasFetching) return;
    const id = setInterval(() => onRefreshRef.current(), 5_000);
    return () => clearInterval(id);
  }, [competitors]);

  // Elapsed time ticker — re-renders every 10s to keep displayed time current
  const [, setTick] = useState(0);
  useEffect(() => {
    const hasFetching = competitors.some((c) => c.fetchStatus === 'fetching');
    if (!hasFetching) return;
    const id = setInterval(() => setTick((t) => t + 1), 10_000);
    return () => clearInterval(id);
  }, [competitors]);

  const handleAddCompetitor = useCallback(async () => {
    const username = compUsername.trim().replace(/^@/, '');
    if (!username) {
      setCompError('Username is required');
      return;
    }
    setCompError(null);
    setCompLoading(true);
    try {
      await socialMediaApi.addCompetitor({ platform: compPlatform, username });
      setCompUsername('');
      onRefresh();
    } catch (err) {
      setCompError(err instanceof Error ? err.message : 'Failed to add competitor');
    } finally {
      setCompLoading(false);
    }
  }, [compPlatform, compUsername, onRefresh]);

  const handleDeleteCompetitor = useCallback(
    async (competitorId: string) => {
      setDeletingId(competitorId);
      try {
        await socialMediaApi.deleteCompetitor(competitorId);
        onRefresh();
      } catch {
        // error silently — refresh will show current state
      } finally {
        setDeletingId(null);
      }
    },
    [onRefresh],
  );

  const handleFetchCompetitor = useCallback(
    async (competitorId: string) => {
      setFetchingId(competitorId);
      try {
        await socialMediaApi.triggerCompetitorFetch(competitorId);
        onRefresh();
      } catch {
        /* no-op */
      } finally {
        setFetchingId(null);
      }
    },
    [onRefresh],
  );

  const handleAbortFetch = useCallback(
    async (competitorId: string) => {
      setAbortingId(competitorId);
      try {
        await socialMediaApi.abortCompetitorFetch(competitorId);
        onRefresh();
      } catch {
        /* no-op */
      } finally {
        setAbortingId(null);
      }
    },
    [onRefresh],
  );

  const handleFetchManualUrl = useCallback(async () => {
    const url = manualUrl.trim();
    if (!url) return;
    setManualFetchLoading(true);
    setManualFetchError(null);
    try {
      const { text } = await socialMediaApi.fetchManualInspirationUrl(url);
      setManualText(text);
      setManualShowTextarea(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Could not fetch URL';
      setManualFetchError(msg);
      setManualShowTextarea(true);
    } finally {
      setManualFetchLoading(false);
    }
  }, [manualUrl]);

  const handleSubmitManual = useCallback(async () => {
    const text = manualText.trim();
    if (!text) {
      setManualError('Content text is required');
      return;
    }
    setManualError(null);
    setManualLoading(true);
    try {
      await socialMediaApi.addManualInspiration({
        text,
        title: manualTitle.trim() || undefined,
        sourceUrl: manualUrl.trim() || undefined,
      });
      setManualText('');
      setManualTitle('');
      setManualUrl('');
      setManualShowTextarea(false);
      setManualFetchError(null);
      setManualSuccess(true);
      setTimeout(() => setManualSuccess(false), 3000);
      onRefresh();
    } catch (err) {
      setManualError(err instanceof Error ? err.message : 'Failed to save content');
    } finally {
      setManualLoading(false);
    }
  }, [manualText, manualTitle, manualUrl, onRefresh]);

  const handleAddRss = useCallback(async () => {
    const title = rssTitle.trim();
    const url = rssUrl.trim();
    if (!title) {
      setRssError('Title is required');
      return;
    }
    if (!url) {
      setRssError('URL is required');
      return;
    }
    setRssError(null);
    setRssLoading(true);
    try {
      await socialMediaApi.addRssFeed({ title, url });
      setRssTitle('');
      setRssUrl('');
      onRefresh();
    } catch (err) {
      setRssError(err instanceof Error ? err.message : 'Failed to add feed');
    } finally {
      setRssLoading(false);
    }
  }, [rssTitle, rssUrl, onRefresh]);

  const handleDeleteRss = useCallback(
    async (feedId: string) => {
      setDeletingId(feedId);
      try {
        await socialMediaApi.deleteRssFeed(feedId);
        onRefresh();
      } catch {
        /* no-op */
      } finally {
        setDeletingId(null);
      }
    },
    [onRefresh],
  );

  const handleFetchRss = useCallback(
    async (feedId: string) => {
      setFetchingId(feedId);
      try {
        await socialMediaApi.triggerRssFeedFetch(feedId);
        onRefresh();
      } catch {
        /* no-op */
      } finally {
        setFetchingId(null);
      }
    },
    [onRefresh],
  );

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-xl rounded-2xl bg-card border border-border shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border border-dashed">
          <h2 className="text-primary text-xl font-normal font-satoshi">Content Sources</h2>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tabs */}
        <div className="w-full p-6">
          <div className="flex shadow-[var(--shadow-soft-blue)] rounded-full p-1 gap-2">
            {(['competitors', 'rss', 'manual'] as Tab[]).map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={cn(
                  'flex-1 py-1 text-sm font-normal transition-colors rounded-full uppercase font-mono',
                  tab === t
                    ? 'text-secondary bg-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted',
                )}
              >
                {t === 'competitors' ? 'Competitors' : t === 'rss' ? 'RSS' : 'Manual'}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
          {tab === 'competitors' ? (
            <>
              {/* Add competitor form */}
              <div className="space-y-2">
                {/* Platform selector */}
                <div className="flex gap-2">
                  {(['instagram', 'twitter'] as CompetitorPlatform[])
                    .filter((p) => p !== 'instagram' || FEATURE_FLAGS.instagramCompetitors)
                    .map((p) => (
                      <button
                        key={p}
                        onClick={() => setCompPlatform(p)}
                        className={cn(
                          'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors',
                          compPlatform === p
                            ? 'bg-primary/10 border-primary text-primary'
                            : 'border-border text-muted-foreground hover:text-foreground hover:bg-muted',
                        )}
                      >
                        {PLATFORM_ICONS[p]}
                        {PLATFORM_LABELS[p]}
                      </button>
                    ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="@username"
                    value={compUsername}
                    onChange={(e) => setCompUsername(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddCompetitor();
                    }}
                    className="flex-1 shadow-social-card resize-none w-full rounded-xl border border-border bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150"
                  />
                  <Button
                    onClick={handleAddCompetitor}
                    disabled={compLoading}
                    size="sm"
                    className="gap-1.5 font-mono text-[12px]"
                  >
                    {compLoading ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Plus className="h-3 w-3" />
                    )}
                    Add
                  </Button>
                </div>
                <p className="text-[10px] text-muted-foreground font-mono ">
                  Track a competitor account to pull their posts into your inspiration feed.
                </p>
                {compError && <p className="text-xs text-destructive">{compError}</p>}
              </div>

              {/* Competitor list */}
              {competitors.filter(
                (c) => c.platform !== 'instagram' || FEATURE_FLAGS.instagramCompetitors,
              ).length > 0 && (
                <div className="divide-y divide-border rounded-xl border border-border overflow-hidden">
                  {competitors
                    .filter((c) => c.platform !== 'instagram' || FEATURE_FLAGS.instagramCompetitors)
                    .map((c) => (
                      <div
                        key={c.competitorId}
                        className="flex items-center gap-3 px-4 py-3 bg-card"
                      >
                        <span className="text-muted-foreground shrink-0">
                          {PLATFORM_ICONS[c.platform]}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">@{c.username}</p>
                          {c.fetchStatus === 'fetching' && (
                            <p className="text-[11px] text-muted-foreground flex items-center gap-1 font-mono">
                              <Loader2 className="h-2.5 w-2.5 animate-spin" />
                              Fetching posts
                              {c.fetchStartedAt &&
                                (() => {
                                  const elapsed = Math.floor(
                                    (Date.now() - new Date(c.fetchStartedAt).getTime()) / 60_000,
                                  );
                                  return elapsed > 0 ? ` — ${elapsed}m elapsed` : null;
                                })()}
                            </p>
                          )}
                          {c.fetchStatus === 'error' && (
                            <p
                              className="text-[11px] text-destructive truncate font-mono"
                              title={c.fetchError ?? undefined}
                            >
                              {c.fetchError ?? 'Fetch failed'} — try again
                            </p>
                          )}
                          {c.fetchStatus === 'idle' && c.lastFetchedAt && (
                            <p className="text-[11px] text-muted-foreground font-mono">
                              Last fetched {new Date(c.lastFetchedAt).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        {c.fetchStatus === 'fetching' ? (
                          <button
                            onClick={() => handleAbortFetch(c.competitorId)}
                            disabled={abortingId === c.competitorId}
                            className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                            title="Cancel fetch"
                          >
                            {abortingId === c.competitorId ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Ban className="h-3.5 w-3.5" />
                            )}
                          </button>
                        ) : (
                          <button
                            onClick={() => handleFetchCompetitor(c.competitorId)}
                            disabled={fetchingId === c.competitorId}
                            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
                            title="Fetch now"
                          >
                            {fetchingId === c.competitorId ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <RefreshCw className="h-3.5 w-3.5" />
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => handleDeleteCompetitor(c.competitorId)}
                          disabled={deletingId === c.competitorId}
                          className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                          title="Remove"
                        >
                          {deletingId === c.competitorId ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="h-3.5 w-3.5" />
                          )}
                        </button>
                      </div>
                    ))}
                </div>
              )}

              {competitors.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-4">
                  No competitors tracked yet.
                </p>
              )}
            </>
          ) : tab === 'manual' ? (
            <>
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Add a blog post or article as inspiration. Paste a URL to auto-extract the
                  content, or write / paste the text directly.
                </p>

                {/* Optional URL + fetch */}
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-foreground">URL (optional)</p>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Link className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                      <Input
                        placeholder="https://example.com/blog/post"
                        value={manualUrl}
                        onChange={(e) => {
                          setManualUrl(e.target.value);
                          setManualFetchError(null);
                          if (!e.target.value.trim()) {
                            setManualShowTextarea(false);
                            setManualText('');
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && manualUrl.trim()) void handleFetchManualUrl();
                        }}
                        className="flex-1 shadow-social-card resize-none w-full rounded-xl border border-border bg-background px-3 py-2 pl-9 text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150"
                      />
                    </div>
                    <Button
                      onClick={() => void handleFetchManualUrl()}
                      disabled={manualFetchLoading || !manualUrl.trim()}
                      size="sm"
                      variant="outline"
                      className="shrink-0 gap-1.5"
                    >
                      {manualFetchLoading ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3.5 w-3.5" />
                      )}
                      Fetch
                    </Button>
                  </div>
                  {manualFetchError && (
                    <div className="flex items-start gap-1.5 text-xs text-destructive">
                      <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                      <span>{manualFetchError} Paste the content manually below.</span>
                    </div>
                  )}
                </div>

                {/* Text area — shown when: no URL, fetch succeeded, or fetch failed */}
                {(manualShowTextarea || !manualUrl.trim()) && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-medium text-foreground">Content</p>
                    <textarea
                      rows={6}
                      placeholder="Paste blog copy, article text, or a post idea here…"
                      value={manualText}
                      onChange={(e) => setManualText(e.target.value)}
                      className="flex-1 shadow-social-card resize-none w-full rounded-xl border border-border bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150"
                    />
                  </div>
                )}

                {/* Optional title */}
                <div className="space-y-1.5">
                  <p className="text-xs font-medium text-foreground">Title / Label (optional)</p>
                  <div className="relative">
                    <Pencil className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                    <Input
                      placeholder="e.g. TechCrunch — AI trends 2025"
                      value={manualTitle}
                      onChange={(e) => setManualTitle(e.target.value)}
                      className="flex-1 shadow-social-card resize-none w-full rounded-xl border border-border bg-background px-3 py-2 pl-9 text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150"
                    />
                  </div>
                </div>

                {manualError && <p className="text-xs text-destructive">{manualError}</p>}
                {manualSuccess && (
                  <p className="text-xs text-green-600">Content added to your inspiration feed.</p>
                )}

                <Button
                  onClick={() => void handleSubmitManual()}
                  disabled={
                    manualLoading ||
                    (!manualText.trim() && !!manualUrl.trim() && !manualShowTextarea)
                  }
                  size="sm"
                  className="w-full gap-1.5"
                >
                  {manualLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Plus className="h-3.5 w-3.5" />
                  )}
                  Add to inspiration feed
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Add RSS form */}
              <div className="space-y-2">
                <p className="text-[10px] text-muted-foreground font-mono">
                  Subscribe to an RSS or Atom feed to pull articles into your inspiration feed.
                </p>
                <Input
                  placeholder="Feed title (e.g. TechCrunch)"
                  value={rssTitle}
                  onChange={(e) => setRssTitle(e.target.value)}
                  className="flex-1 shadow-social-card resize-none w-full rounded-xl border border-border bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150"
                />
                <div className="flex gap-2">
                  <Input
                    placeholder="https://example.com/feed.xml"
                    value={rssUrl}
                    onChange={(e) => setRssUrl(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleAddRss();
                    }}
                    className="flex-1 shadow-social-card resize-none w-full rounded-xl border border-border bg-background px-3 py-2 text-foreground placeholder:text-muted-foreground/50 outline-none focus:border-primary leading-relaxed rounded-[10px] outline-none transition-all duration-150"
                  />
                  <Button
                    onClick={handleAddRss}
                    disabled={rssLoading}
                    size="sm"
                    className="gap-1.5 font-mono text-[12px]"
                  >
                    {compLoading ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Plus className="h-3 w-3" />
                    )}
                    Add
                  </Button>
                </div>
                {rssError && <p className="text-xs text-destructive">{rssError}</p>}
              </div>

              {/* RSS list */}
              {rssFeeds.length > 0 && (
                <div className="divide-y divide-border rounded-xl border border-border overflow-hidden">
                  {rssFeeds.map((f) => (
                    <div key={f.feedId} className="flex items-center gap-3 px-4 py-3 bg-card">
                      <Rss className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{f.title}</p>
                        <p className="text-[11px] text-muted-foreground truncate">{f.url}</p>
                        {f.lastFetchedAt && (
                          <p className="text-[11px] text-muted-foreground">
                            Last fetched {new Date(f.lastFetchedAt).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => handleFetchRss(f.feedId)}
                        disabled={fetchingId === f.feedId}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
                        title="Fetch now"
                      >
                        {fetchingId === f.feedId ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3.5 w-3.5" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDeleteRss(f.feedId)}
                        disabled={deletingId === f.feedId}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
                        title="Remove"
                      >
                        {deletingId === f.feedId ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {rssFeeds.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-4">
                  No RSS feeds added yet.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
